import React from "react";
import FullLayout from "@/layouts/FullLayout";
import { primaryButtonClasses } from "@/helpers/style.helpers";
import { fetchWorktables } from "@/features/worktable/api";
import { scannerMenuItems } from "./menuItems";
import { buildNavigationQrImageUrl, buildNavigationQrUrl } from "./helpers";

const QrCodeOverview: React.FC = () => {
  const [worktableItems, setWorktableItems] = React.useState<Array<{ id: string; label: string; path: string; description: string }>>([]);

  React.useEffect(() => {
    fetchWorktables()
      .then((worktables) =>
        setWorktableItems(
          worktables.map((worktable) => ({
            id: `worktable-${worktable.id}`,
            label: `Werktafel ${worktable.name}`,
            path: `/worktable/${worktable.id}`,
            description: "Directe link naar deze werktafel",
          }))
        )
      )
      .catch(() => setWorktableItems([]));
  }, []);

  const printableItems = [...scannerMenuItems.filter((item) => !item.isDeveloperOnly), ...worktableItems];

  const downloadQrCode = async (label: string, path: string) => {
    const response = await fetch(buildNavigationQrImageUrl(path, "svg"));
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = downloadUrl;
    anchor.download = `${label.toLowerCase().replace(/\s+/g, "-")}-qr.svg`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(downloadUrl);
  };

  const handleDownloadAll = async () => {
    for (const item of printableItems) {
      // Sequential downloads reduce the chance that the browser collapses them into a single blocked popup action.
      await downloadQrCode(item.label, item.path);
    }
  };

  return (
    <FullLayout>
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-col gap-3 rounded bg-white p-4 shadow-sm print:shadow-none">
          <h1 className="text-2xl font-bold">Scanner QR codes</h1>
          <p className="text-sm text-blue-900">
            Scan een QR-code om direct naar de juiste scannerpagina te gaan. Gebruik deze pagina om de vier QR-codes
            te downloaden of af te drukken.
          </p>
          <button className={`${primaryButtonClasses} print:hidden`} onClick={handleDownloadAll} type="button">
            Download alle QR-codes
          </button>
          <button className={`${primaryButtonClasses} print:hidden`} onClick={() => window.print()} type="button">
            Print QR codes
          </button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {printableItems.map((item) => {
            const targetUrl = buildNavigationQrUrl(item.path);

            return (
              <article
                key={item.id}
                className="break-inside-avoid rounded bg-white p-6 shadow-sm print:border print:border-gray-300 print:shadow-none"
              >
                <div className="space-y-4 text-center">
                  <div>
                    <h2 className="text-2xl font-bold">{item.label}</h2>
                    <p className="text-sm text-blue-900">{item.description}</p>
                  </div>
                  <img
                    alt={`QR code voor ${item.label}`}
                    className="mx-auto h-64 w-64"
                    height={256}
                    src={buildNavigationQrImageUrl(item.path, "svg")}
                    width={256}
                  />
                  <p className="break-all rounded bg-blue-50 px-3 py-2 text-xs text-blue-900">{targetUrl}</p>
                  <button
                    className={`${primaryButtonClasses} w-full print:hidden`}
                    onClick={() => {
                      void downloadQrCode(item.label, item.path);
                    }}
                    type="button"
                  >
                    Download QR-code
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </FullLayout>
  );
};

export default QrCodeOverview;
